import React, { useEffect, useState } from 'react';
import { Loader, Text } from './';
import { api } from '../_library';

type Props = {
    transactionId: string | number;
};

/**
 * Renders the uploaded receipt image for a transaction.
 *
 * The API is authenticated with a Bearer token held in localStorage, so the
 * image cannot simply be pointed at with <img src="/transactions/x/receipt">
 * (the browser would send no Authorization header). Instead the file is
 * fetched as a blob through the shared api client and turned into an object
 * URL, which is revoked again on unmount so the blob is not leaked.
 */
export const ReceiptPreview: (props: Props) => JSX.Element = ({ transactionId }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        setIsLoading(true);
        setError('');

        api.get(`/transactions/${transactionId}/receipt`, { responseType: 'blob' })
            .then(res => {
                if (cancelled) return;

                objectUrl = window.URL.createObjectURL(res.data);
                setImageUrl(objectUrl);
                setIsLoading(false);
            })
            .catch(apiResponse => {
                if (cancelled) return;

                // responseType 'blob' means an error body is a Blob too, not JSON,
                // so branch on the status code rather than parsing data.error
                setError(
                    apiResponse.response?.status === 404
                        ? 'bk.transaction_receipt.preview.error.not_found'
                        : 'bk.transaction_receipt.preview.error.general'
                );
                setIsLoading(false);
            });

        return () => {
            cancelled = true;

            if (objectUrl) {
                window.URL.revokeObjectURL(objectUrl);
            }
        };
    }, [transactionId]);

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Text left error label={error} />;
    }

    return (
        <div className="flex justify-center">
            <img
                src={imageUrl as string}
                alt=""
                className="max-w-full max-h-[70vh] object-contain"
            />
        </div>
    );
};
